<template>
  <div>
    <div class="d-flex justify-space-between align-center gap-2">
      <v-text-field
        v-model="search"
        label="Buscar"
        single-line
        hide-details
        outlined
        dense
      />

      <v-btn
        icon
        @click="exportToExcel"
        :disabled="loading || loadingExport"
        :loading="loadingExport"
      >
        <v-icon>mdi-file-excel</v-icon>
      </v-btn>
      <v-btn
        icon
        @click="getPayments"
        :disabled="temporaryDisabled"
        :loading="loading"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-alert v-if="error" type="error">
      {{ error }}
      <v-btn @click="getPayments">Tentar novamente</v-btn>
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="payments"
      :loading="loading"
      :search="search"
      sort-by="createdAt"
      sort-desc
    >
      <template v-slot:item.id="{ item }">
        <span class="text-overline">#{{ item.id | shortId }}</span>
      </template>
      <template v-slot:item.amount="{ item }">
        {{ item.amount | currency }}
      </template>
      <template v-slot:item.date="{ item }">
        {{ item.date | date }}
      </template>
      <template v-slot:item.paymentMethod="{ item }">
        {{
          PAYMENT.paymentMethod[item.paymentMethod]?.text || item.paymentMethod
        }}
      </template>

      <template v-slot:item.paymentType="{ item }">
        {{ PAYMENT.paymentType[item.paymentType]?.text || item.paymentType }}
        <v-chip x-small class="px-2" v-if="item.paymentType === 'CREDIT_CARD'">
          {{ item.installments === 1 ? "à vista" : item.installments + "x" }}
        </v-chip>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip
          :color="PAYMENT.ticketStatus[item.status]?.color || 'grey'"
          small
          label
          class="w-full justify-center px-2"
        >
          {{ PAYMENT.ticketStatus[item.status]?.text || item.status }}
        </v-chip>
      </template>
      <template v-slot:item.createdAt="{ item }">
        {{ item.createdAt | date("DD/MM/YYYY HH:mm") }}
      </template>
      <template v-slot:no-data>
        Nenhum pagamento encontrado para este evento.
      </template>
    </v-data-table>
  </div>
</template>

<script>
import PARTY from "@/services/admin/parties";
import PAYMENT from "@/utils/payment";
import ExcelJS from "exceljs";
import moment from "moment";

export default {
  name: "PartyPayments",
  props: {
    party: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      payments: [],
      loadingExport: false,
      temporaryDisabled: false,
      headers: [
        { text: "ID", value: "id" },
        { text: "Valor", value: "amount" },
        { text: "Ingresso", value: "_count.Ticket" },
        { text: "Método de Pagamento", value: "paymentMethod" },
        { text: "Tipo de Pagamento", value: "paymentType" },
        { text: "Status", value: "status" },
        { text: "Data", value: "createdAt", align: "end" },
      ],
      search: "",
      PAYMENT,
    };
  },
  methods: {
    async getPayments() {
      this.loading = true;
      this.error = null;
      try {
        const data = await PARTY.payments(this.party.id);
        this.payments = data.payments;

        this.disableButton(3000);
      } catch (error) {
        console.error("Erro ao buscar pagamentos:", error);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    disableButton(time) {
      this.temporaryDisabled = true;
      setTimeout(() => {
        this.temporaryDisabled = false;
      }, time);
    },
    async exportToExcel() {
      this.loadingExport = true;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Pagamentos");

      // Adiciona os cabeçalhos das colunas
      worksheet.columns = [
        { header: "ID", key: "id", width: 10 },
        { header: "Ingresso", key: "ticketCount", width: 15 },
        { header: "Valor", key: "amount", width: 15 },
        { header: "Taxa de Cliente", key: "clientFee", width: 15 },
        { header: "Taxa da Plataforma", key: "platformFee", width: 15 },
        { header: "Método de Pagamento", key: "paymentMethod", width: 20 },
        { header: "Tipo de Pagamento", key: "paymentType", width: 20 },
        { header: "Parcelas", key: "installments", width: 10 },
        { header: "Taxa de Parcelamento", key: "installmentsFee", width: 20 },
        { header: "Vendedor", key: "seller", width: 20 },
        { header: "Status", key: "status", width: 20 },
        { header: "Data", key: "createdAt", width: 20 },
      ];

      // Adiciona os dados dos pagamentos
      this.payments.forEach((payment) => {
        const row = worksheet.addRow({
          id: `#${payment.id.slice(0, 8)}`,
          amount: payment.amount,
          clientFee: payment.clientFee || 0,
          platformFee: payment.platformFee || 0,
          ticketCount: payment._count.Ticket,
          paymentMethod:
            PAYMENT.paymentMethod[payment.paymentMethod]?.text ||
            payment.paymentMethod,
          paymentType:
            PAYMENT.paymentType[payment.paymentType]?.text ||
            payment.paymentType,
          installments: payment.installments || 1,
          installmentsFee: payment.installmentsFee || 0,
          seller: payment.PosSession?.name || null,
          status: PAYMENT.ticketStatus[payment.status]?.text || payment.status,
          createdAt: moment(payment.createdAt).format("DD/MM/YYYY HH:mm"),
        });

        row.getCell("amount").numFmt = "R$ #,##0.00";
        row.getCell("clientFee").numFmt = "R$ #,##0.00";
        row.getCell("platformFee").numFmt = "R$ #,##0.00";
        row.getCell("installments").numFmt = "#";
        row.getCell("installmentsFee").numFmt = "R$ #,##0.00";
        row.getCell("createdAt").numFmt = "DD/MM/YYYY HH:mm";
      });

      // Salva o arquivo Excel
      const excelBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      const name = `pagamentos-${this.normalizeName(
        this.party.name
      )}-${moment().format("DD-MM-YYYY-HH-mm")}.xlsx`;
      setTimeout(() => {
        this.saveAs(blob, name);
        this.loadingExport = false;
      }, 1000);
    },
    saveAs(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    },
    normalizeName(name) {
      return name.replace(/ /g, "_");
    },
  },
  mounted() {
    this.getPayments();
  },
};
</script>
